<template>
  <div>
    <b-col md="12">
      <div>
        <b-card>
          <div>
            <!-- Selection Actions and Title -->
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h2>{{ $t("incubator.nicu-invoices") }}</h2>
              <div class="d-flex gap-3">
                <b-button
                  v-if="canCreateInvoice"
                  variant="success"
                  size="sm"
                  class="mr-2"
                  @click="openCreateInvoiceModal"
                >
                  <feather-icon
                    icon="PlusIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ $t("incubator.create-invoice") }}
                </b-button>
                <b-button
                  v-if="!allSelected"
                  variant="outline-primary"
                  size="sm"
                  @click="selectAll"
                >
                  <feather-icon
                    icon="CheckSquareIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ $t("common.select-all") }}
                </b-button>
                <b-button
                  v-if="canBulkUpdate"
                  class="ml-2 mr-2"
                  variant="warning"
                  size="sm"
                  @click="showBulkUpdateModal"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="14"
                    class="mr-1"
                  />
                  الموافقة على الفواتير ({{ selectedInvoices.length }})
                </b-button>
                <b-button
                  v-if="canBulkPrint"
                  variant="success"
                  size="sm"
                  @click="bulkPrintSummary"
                >
                  <feather-icon
                    icon="PrinterIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ $t("common.print-list") }}
                </b-button>
                <b-button
                  v-if="canBulkPrint"
                  variant="info"
                  size="sm"
                  @click="bulkPrintDetailed"
                >
                  <feather-icon
                    icon="FileTextIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ $t("common.print-detailed") }}
                </b-button>
                <b-button
                  v-if="selectedInvoices.length > 0"
                  variant="outline-secondary"
                  size="sm"
                  @click="clearSelection"
                >
                  {{ $t("common.clear-selection") }}
                </b-button>
              </div>
            </div>

            <!-- Bulk Selection Summary -->
            <b-row
              v-if="selectedInvoices.length > 0"
              class="mb-3"
            >
              <b-col cols="12">
                <b-alert
                  show
                  variant="info"
                  class="mb-0"
                >
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{{ $t("common.selected") }}: {{ selectedInvoices.length }} {{ $t("common.invoices") }}</strong>
                    </div>
                    <div>
                      <strong>{{ $t("common.total") }}: <span class="text-success">{{ formatPrice(selectedInvoicesTotal) }}</span></strong>
                    </div>
                  </div>
                </b-alert>
              </b-col>
            </b-row>

            <!-- Data Table -->
            <b-table
              v-if="!loading"
              :items="this.invoices.data"
              responsive
              :fields="tableColumns"
              primary-key="id"
              show-empty
              empty-text="No matching results found"
              class="position-relative"
            >
              <!-- Checkbox for selection -->
              <template #cell(select)="data">
                <b-form-checkbox
                  v-model="selectedInvoices"
                  :value="data.item"
                  @change="onSelectionChange"
                />
              </template>

              <!-- Invoice Number -->
              <template #cell(invoice_no)="data">
                <span class="font-weight-bold text-primary">
                  #{{ data.item.invoice_no }}
                </span>
              </template>

              <!-- Patient Name -->
              <template #cell(patient_name)="data">
                <div class="d-flex flex-column text-nowrap">
                  <span class="font-weight-bold text-primary">
                    {{ formatPatientName(data.item) }}
                  </span>
                  <small class="text-muted">
                    {{ getNestedProperty(data.item, 'patient_card.full_companion_name', '') }}
                  </small>
                </div>
              </template>

              <!-- Incubator Info -->
              <template #cell(incubator_info)="data">
                <div class="d-flex flex-column">
                  <span class="font-weight-bold">
                    {{ getNestedProperty(data.item, 'patient_card_incubator.incubator.name', 'N/A') }}
                  </span>
                  <small class="text-muted">
                    {{ getNestedProperty(data.item, 'patient_card_incubator.start_time', '') }}
                  </small>
                </div>
              </template>

              <!-- Discount -->
              <template #cell(discount)="data">
                <span class="text-nowrap font-weight-bold text-warning">
                  {{ formatPrice(data.item.discount || 0) }}
                </span>
              </template>

              <!-- Amount -->
              <template #cell(amount)="data">
                <span class="text-nowrap font-weight-bolder text-success">
                  {{ formatPrice(data.item.amount) }}
                </span>
              </template>

              <!-- Date -->
              <template #cell(date)="data">
                <span class="text-nowrap">{{ data.item.date }}</span>
              </template>

              <!-- Status -->
              <template #cell(status)="data">
                <div>
                  <ChangeInvoiceStatusDropDown
                    name="nicuInvoice"
                    endpoint="invoices"
                    :data="data.item"
                    @onEmit="$emit('onChangeStatus')"
                  />
                </div>
              </template>

              <!-- Actions -->
              <template #cell(actions)="data">
                <div class="text-nowrap d-flex">
                  <b-button
                    v-if="data.item.status == 'approved'"
                    variant="outline-primary"
                    size="sm"
                    class="mx-1"
                    @click="showNICUInvoice(data.item)"
                  >
                    {{ $t("reception.Print Invoice") }}
                  </b-button>
                  <b-button
                    v-if="$store.state.app.permissions.includes('apply-invoice-discount') && data.item.status !== 'printed' && data.item.status !== 'approved'"
                    variant="outline-warning"
                    size="sm"
                    class="mx-1"
                    @click="showDiscountModal(data.item)"
                  >
                    <feather-icon
                      icon="PercentIcon"
                      size="14"
                      class="mr-1"
                    />
                    {{ $t("common.apply-discount") }}
                  </b-button>
                </div>
              </template>
            </b-table>

            <template v-if="loading">
              <div>
                <div class="d-flex justify-content-center mb-1">
                  <b-spinner label="Loading..." />
                </div>
              </div>
            </template>

            <div class="mx-2 mb-2">
              <b-row>
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-start"
                >
                  <span
                    class="text-muted"
                  >{{ $t("common.showing") }} 1 {{ $t("common.to") }} 10
                  </span>
                </b-col>
                <!-- Pagination -->
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-end"
                  @click="getInvoices()"
                >
                  <b-pagination
                    v-model="page"
                    :total-rows="dataCount"
                    :per-page="take"
                    first-number
                    last-number
                    class="mb-0 mt-1 mt-sm-0"
                    prev-class="prev-item"
                    next-class="next-item"
                  >
                    <template #prev-text>
                      <feather-icon
                        icon="ChevronLeftIcon"
                        size="18"
                      />
                    </template>
                  </b-pagination>
                </b-col>
              </b-row>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Print Invoice Component -->
      <div ref="printContent">
        <NICUInvoice
          :patient-data="patientData"
          :data="invoice"
          :incubator-name="getNestedProperty(invoice, 'patient_card_incubator.incubator.name', 'N/A')"
        />
      </div>

      <!-- Print Modal Confirmation -->
      <b-modal
        v-for="item in invoices.data"
        :id="`modal-confirm-print-${item.id}`"
        :key="`print-modal-${item.id}`"
        :no-close-on-backdrop="true"
        :title="$t('incubator.print-nicu-invoice')"
        ok-only
        :ok-title="$t('common.print')"
        @ok="generatePdf"
      >
        <b-card-text>
          <h5>{{ $t("incubator.print-nicu-invoice") }}</h5>
          {{ $t("surgery.print-sure") }}
        </b-card-text>
      </b-modal>

      <InvoiceSummaryPrint
        id="BulkPrintSummaryNICU"
        :invoices="bulkPrintInvoicesData"
        :total-amount="bulkPrintTotal"
        invoice-type="nicu"
      />

      <!-- Shared Invoice List PDF Component -->
      <InvoicesListPdf
        v-if="listPrintItemsNICU.length > 0"
        ref="nicuInvoicesListPdf"
        :patient-name="listPrintPatientName"
        :print-date="new Date()"
        :items="listPrintItemsNICU"
        :title="$t('incubator.nicu-invoices')"
        filename="nicu-invoices-list"
        @hasStartedGeneration="isGeneratingPdf = true"
        @hasGenerated="isGeneratingPdf = false"
      />

      <!-- Loading Overlay -->
      <div
        v-if="isGeneratingPdf"
        class="pdf-loading-overlay"
      >
        <div class="pdf-loading-content">
          <b-spinner
            variant="primary"
            label="Loading..."
          />
          <p class="mt-2">
            {{ $t("common.generating-pdf") || "جاري إنشاء PDF..." }}
          </p>
        </div>
      </div>

      <!-- Bulk Print Detailed Components -->
      <div
        id="BulkPrintNICUInvoices"
        style="display: none;"
      >
        <div
          v-for="inv in bulkPrintInvoicesData"
          :key="`bulk-nicu-${inv.id}`"
          class="print-page-break"
        >
          <NICUInvoice
            :patient-data="getBulkPatientData(inv)"
            :data="inv"
            :incubator-name="getBulkIncubatorName(inv)"
          />
        </div>
      </div>

      <!-- Hidden print buttons -->
      <b-button
        v-if="bulkPrintInvoicesData.length > 0"
        ref="BulkPrintSummaryRef"
        v-print="BulkPrintSummaryConfig"
        variant="outline-primary"
        size="sm"
        class="hidden"
      >
        {{ $t("common.print-summary") }}
      </b-button>

      <b-button
        v-if="bulkPrintInvoicesData.length > 0"
        ref="BulkPrintRef"
        v-print="BulkPrintConfig"
        variant="outline-primary"
        size="sm"
        class="hidden"
      >
        {{ $t("common.print-detailed") }}
      </b-button>

      <!-- Bulk Update Status Modal -->
      <b-modal
        id="modal-bulk-update-status"
        :no-close-on-backdrop="true"
        title="الموافقة على الفواتير المعلقة"
        ok-only
        ok-title="الموافقة على الفواتير"
        @ok="bulkUpdateStatus"
      >
        <b-card-text>
          <h5>الموافقة على الفواتير المعلقة</h5>
          <p>{{ $t('common.selected-invoices-count', { count: selectedInvoices.length }) }}</p>

          <p class="font-weight-bold">
            هل تريد الموافقة على جميع الفواتير المعلقة المحددة؟
          </p>

          <div class="mt-3">
            <h6>{{ $t('common.selected-invoices') }}:</h6>
            <ul class="list-unstyled">
              <li
                v-for="invoice in selectedInvoices"
                :key="invoice.id"
                class="mb-1"
              >
                <small class="text-muted">
                  #{{ getNestedProperty(invoice, 'invoice_no') }} -
                  {{ formatPrice(getNestedProperty(invoice, 'amount')) }}
                </small>
              </li>
            </ul>
          </div>
        </b-card-text>
      </b-modal>

      <!-- Modal to apply discount -->
      <b-modal
        id="modal-apply-discount"
        centered
        size="md"
        :title="$t('common.apply-discount')"
        @ok="applyDiscount"
      >
        <b-card-text>
          <b-form-group
            :label="$t('common.invoice-number')"
            label-for="discount-invoice-no"
          >
            <b-form-input
              id="discount-invoice-no"
              :value="'#' + (discountForm.invoice_no || '')"
              disabled
            />
          </b-form-group>

          <b-form-group
            :label="$t('common.current-amount')"
            label-for="discount-current-amount"
          >
            <b-form-input
              id="discount-current-amount"
              :value="formatPrice(discountForm.amount)"
              disabled
            />
          </b-form-group>

          <b-form-group
            :label="$t('common.discount-amount')"
            label-for="discount-amount"
            :invalid-feedback="discountError"
            :state="discountError ? false : null"
          >
            <b-form-input
              id="discount-amount"
              v-model="discountForm.discount"
              type="number"
              min="0"
              :placeholder="$t('common.enter-discount-amount')"
              :state="discountError ? false : null"
            />
          </b-form-group>

          <b-alert
            v-if="discountForm.discount > 0"
            variant="info"
            show
            class="mt-2"
          >
            <div class="d-flex justify-content-between">
              <span>{{ $t('common.amount-after-discount') }}:</span>
              <strong>{{ formatPrice(calculateAmountAfterDiscount()) }}</strong>
            </div>
          </b-alert>
        </b-card-text>
      </b-modal>

      <!-- Create New Invoice Modal -->
      <b-modal
        id="modal-create-invoice"
        :title="$t('incubator.create-new-invoice')"
        size="md"
        hide-footer
        centered
        :no-close-on-backdrop="true"
      >
        <b-card-text>
          <!-- Patient Info (Read-only Display) -->
          <b-alert
            variant="info"
            show
            class="mb-1"
          >
            <div class="d-flex flex-column">
              <span><strong>{{ $t("reception.patient-name") }}:</strong> {{ newInvoicePatientName }}</span>

              <span><strong class="mt-2 inline-block">{{ $t("incubator.incubator") }}:</strong> {{ newInvoiceIncubatorName }}</span>
            </div>
          </b-alert>

          <!-- Amount Input -->
          <b-form-group
            :label="$t('incubator.amount')"
            label-for="invoice_amount"
          >
            <b-form-input
              id="invoice_amount"
              v-model="newInvoiceForm.amount"
              type="number"
              min="0"
              :placeholder="$t('incubator.amount')"
            />
            <p class="text-danger mt-1">
              {{ newInvoiceError.amount }}
            </p>
          </b-form-group>

          <!-- Paid Until Input -->
          <b-form-group
            :label="$t('incubator.paid-until')"
            label-for="invoice_paid_until"
          >
            <div class="d-flex align-items-center">
              <b-form-input
                id="invoice_paid_until"
                v-model="newInvoiceForm.paid_until"
                type="datetime-local"
                :placeholder="$t('incubator.paid-until')"
                :state="newInvoiceError.paid_until ? false : null"
                class="flex-grow-1"
              />
              <b-tooltip
                v-if="currentPaidUntil"
                target="paid-until-info-icon"
                triggers="hover"
                placement="top"
              >
                <strong>{{ $t('incubator.current-paid-until') }}:</strong><br>
                {{ currentPaidUntil }}<br>
                <small>{{ $t('incubator.new-date-must-be-greater') }}</small>
              </b-tooltip>
              <i
                v-if="currentPaidUntil"
                id="paid-until-info-icon"
                class="fas fa-info-circle text-warning ml-2"
                style="font-size: 1.3rem; cursor: help;"
              />
            </div>
            <p class="text-danger mt-1">
              {{ newInvoiceError.paid_until }}
            </p>
          </b-form-group>

          <!-- Description (Optional) -->
          <b-form-group
            :label="$t('common.description')"
            label-for="invoice_description"
          >
            <b-form-textarea
              id="invoice_description"
              v-model="newInvoiceForm.description"
              :placeholder="$t('common.description')"
              rows="3"
            />
          </b-form-group>

          <!-- Action Buttons -->
          <div class="d-flex justify-content-end">
            <b-button
              variant="outline-secondary"
              class="mr-1"
              :disabled="loadingInvoice"
              @click="$bvModal.hide('modal-create-invoice')"
            >
              {{ $t("common.cancel") }}
            </b-button>
            <b-button
              variant="success"
              :disabled="loadingInvoice"
              @click="createNewInvoice"
            >
              <span v-if="!loadingInvoice">
                {{ $t("incubator.create-invoice") }}
              </span>
              <b-spinner
                v-else
                small
              />
            </b-button>
          </div>
        </b-card-text>
      </b-modal>
    </b-col>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BLink,
  BDropdown,
  BSpinner,
  BDropdownItem,
  BTooltip,
  BFormGroup,
  BFormSelect,
  BModal,
  BCardText,
  BFormCheckboxGroup,
  BFormCheckbox,
  BCardBody,
  BPagination,
  BTable,
  BBadge,
  BAlert,
  BFormTextarea,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'
import { getNestedProperty } from '@/utils/nestedProperty.js'

import ActionsButtons from '@/components/common/ActionsButtons.vue'
import DeleteModal from '@/components/modals/DeleteModal.vue'

const AutoComplete = () => import('@/components/common/AutoComplete.vue')
const NICUInvoice = () => import('@/components/printables/NICUInvoice.vue')
const ChangeInvoiceStatusDropDown = () => import('@/components/invoices/ChangeInvoiceStatusDropDown.vue')
const InvoiceSummaryPrint = () => import('@/components/printables/InvoiceSummaryPrint.vue')
const InvoicesListPdf = () => import('@/components/printables/InvoicesListPdf.vue')

export default {
  components: {
    BCard,
    BRow,
    BBadge,
    BCol,
    BFormInput,
    BButton,
    BLink,
    BDropdown,
    BDropdownItem,
    BTooltip,
    vSelect,
    BFormTextarea,
    ToastificationContent,
    VuePhoneNumberInput,
    DeleteModal,
    ActionsButtons,
    BFormGroup,
    BFormSelect,
    BSpinner,
    BModal,
    BCardText,
    BFormCheckbox,
    BFormCheckboxGroup,
    BCardBody,
    BPagination,
    BTable,
    BAlert,
    ChangeInvoiceStatusDropDown,
    NICUInvoice,
    InvoiceSummaryPrint,
    InvoicesListPdf,
  },
  props: ['invoices', 'patientData'],
  data() {
    return {
      tableColumns: [
        {
          key: 'select', label: '', sortable: false, thStyle: { width: '40px' },
        },
        {
          key: 'invoice_no',
          label: this.$t('accounting.invoice-no'),
          sortable: true,
        },
        {
          key: 'patient_name',
          label: this.$t('reception.patient-name'),
          sortable: false,
        },
        {
          key: 'incubator_info',
          label: this.$t('incubator.incubator'),
          sortable: false,
        },
        {
          key: 'amount',
          label: this.$t('incubator.amount'),
          sortable: true,
        },
        {
          key: 'discount',
          label: this.$t('accounting.discount'),
          sortable: true,
        },
        {
          key: 'date',
          label: this.$t('common.date'),
          sortable: true,
        },
        {
          key: 'status',
          sortable: true,
          label: this.$t('common.status'),
        },
        { key: 'actions', label: this.$t('common.actions') },
      ],
      invoiceLatestStatus: '',
      InvoiceId: '',
      invoice: '',
      currentInvoice: null,
      selectedInvoices: [],
      discountForm: {
        id: null,
        invoice_no: '',
        amount: 0,
        discount: 0,
      },
      discountError: '',
      bulkUpdateForm: {
        status: null,
      },
      // new invoice form
      newInvoiceForm: {
        patient_card_id: '',
        patient_card_incubator_id: '',
        amount: '',
        paid_until: '',
        description: '',
        type: 'invoice_nicu',
      },
      newInvoiceError: {
        amount: '',
        paid_until: '',
      },
      loadingInvoice: false,
      newInvoicePatientName: '',
      newInvoiceIncubatorName: '',
      statusOptions: [],
      isGenerating: false,
      isGeneratingPdf: false,
      currentPage: 1,
      takeOptions: [10, 25, 50, 100],
      searchQuery: '',
      sortBy: 'id',
      isSortDirDesc: true,
      page: 1,
      dataCount: 0,
      loading: false,
      take: 10,
      bulkPrintInvoicesData: [],
      BulkPrintConfig: {
        id: 'BulkPrintNICUInvoices',
      },
      BulkPrintSummaryConfig: {
        id: 'BulkPrintSummaryNICU',
      },
      params: {
        form: {},
        path: '',
      },
    }
  },

  computed: {
    allSelected() {
      const invoicesData = getNestedProperty(this.invoices, 'data', [])
      return invoicesData.length > 0 && this.selectedInvoices.length === invoicesData.length
    },

    canCreateInvoice() {
      const invoicesData = getNestedProperty(this.invoices, 'data', [])
      return invoicesData.length > 0 && getNestedProperty(invoicesData[0], 'patient_card_incubator_id')
    },

    canBulkUpdate() {
      if (this.selectedInvoices.length === 0) return false

      // Allow bulk status change only for pending invoices
      return this.selectedInvoices.every(invoice => {
        const status = getNestedProperty(invoice, 'status', '').toLowerCase()
        return status === 'pending'
      })
    },

    canBulkPrint() {
      if (this.selectedInvoices.length === 0) return false
      return this.selectedInvoices.every(invoice => {
        const status = getNestedProperty(invoice, 'status', '').toLowerCase()
        return status === 'approved'
      })
    },

    selectedInvoicesTotal() {
      return this.selectedInvoices.reduce((total, invoice) => {
        const amount = getNestedProperty(invoice, 'amount', 0)
        return total + (parseFloat(amount) || 0)
      }, 0)
    },

    bulkPrintTotal() {
      return this.bulkPrintInvoicesData.reduce((total, invoice) => total + (Number(invoice.amount) || 0), 0)
    },

    currentPaidUntil() {
      const invoicesData = getNestedProperty(this.invoices, 'data', [])
      if (invoicesData.length > 0) {
        return getNestedProperty(invoicesData[0], 'patient_card_incubator.paid_until', '')
      }
      return ''
    },

    listPrintItemsNICU() {
      if (!this.bulkPrintInvoicesData || this.bulkPrintInvoicesData.length === 0) {
        return []
      }
      return this.bulkPrintInvoicesData.map(invoice => ({
        invoice_no: invoice.invoice_no,
        patient_name: getNestedProperty(invoice, 'patient_card.patient.name', 'N/A'),
        name: getNestedProperty(invoice, 'patient_card_incubator.incubator.name', 'NICU Invoice'),
        amount: invoice.amount || 0,
        discount: invoice.discount || 0,
        date: invoice.date || invoice.created_at,
        created_at: invoice.created_at,
        id: invoice.id,
      }))
    },

    listPrintPatientName() {
      if (this.patientData && this.patientData.name) {
        return this.patientData.name
      }
      if (this.bulkPrintInvoicesData && this.bulkPrintInvoicesData.length > 0) {
        const firstInvoice = this.bulkPrintInvoicesData[0]
        return getNestedProperty(firstInvoice, 'patient_card.patient.name', 'N/A')
      }
      return 'N/A'
    },
  },

  mounted() {
    // Component mounted
  },

  methods: {
    getNestedProperty,

    // Format price
    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : '0 IQD'
    },

    // Format patient name with child name
    formatPatientName(item) {
      const parentName = getNestedProperty(item, 'patient_card.patient.name', 'N/A')
      const childName = getNestedProperty(item, 'patient_card.child_name', '')

      // If no child name, return parent name only
      if (!childName) {
        return parentName
      }

      // If child name is "ابن" (son) or "بنت" (daughter), put it before parent name
      if (childName === 'ابن' || childName === 'بنت') {
        return `${childName} ${parentName}`
      }

      // Otherwise, assume child_name is the actual child's name and display both
      return `${childName} (${parentName})`
    },

    // Selection methods
    onSelectionChange() {
      // Triggered when selection changes
    },

    clearSelection() {
      this.selectedInvoices = []
    },

    selectAll() {
      const invoicesData = getNestedProperty(this.invoices, 'data', [])
      this.selectedInvoices = [...invoicesData]
    },

    // Show bulk update modal
    showBulkUpdateModal() {
      if (this.selectedInvoices.length === 0) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.select-invoices-first'),
          },
        })
        return
      }

      // Check if all selected invoices are approved or pending
      const hasInvalidStatus = this.selectedInvoices.some(invoice => {
        const status = getNestedProperty(invoice, 'status', '').toLowerCase()
        return status !== 'approved' && status !== 'pending'
      })

      if (hasInvalidStatus) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'warning',
            text: this.$t('common.only-approved-pending-invoices'),
          },
        })
        return
      }

      this.bulkUpdateForm.status = 'approved'
      this.$bvModal.show('modal-bulk-update-status')
    },

    // Bulk update status
    async bulkUpdateStatus() {
      // Status check removed as we default to approved

      try {
        this.loading = true

        // Prepare the payload
        const payload = {
          invoices: this.selectedInvoices.map(invoice => ({
            id: invoice.id,
            status: 'approved',
          })),
        }

        // Make bulk update API call
        await this.$http.post('/invoices/bulkUpdateStatus', payload)

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('common.bulk-update-success'),
          },
        })

        // Clear selection and refresh data
        this.clearSelection()
        this.$bvModal.hide('modal-bulk-update-status')

        // Emit event to refresh parent data
        this.$emit('onChangeStatus')
      } catch (error) {
        console.error('Bulk update error:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.bulk-update-error'),
          },
        })
      } finally {
        this.loading = false
      }
    },

    // Show discount modal
    showDiscountModal(invoice) {
      this.discountForm = {
        id: invoice.id,
        invoice_no: invoice.invoice_no || '',
        amount: parseFloat(invoice.amount) || 0,
        discount: parseFloat(invoice.discount) || 0,
      }
      this.discountError = ''
      this.$bvModal.show('modal-apply-discount')
    },

    // Calculate amount after discount
    calculateAmountAfterDiscount() {
      const amount = parseFloat(this.discountForm.amount) || 0
      const discount = parseFloat(this.discountForm.discount) || 0
      return Math.max(0, amount - discount)
    },

    // Apply discount
    async applyDiscount(bvModalEvt) {
      bvModalEvt.preventDefault()

      // Validate discount
      const discount = parseFloat(this.discountForm.discount)
      const amount = parseFloat(this.discountForm.amount)

      if (isNaN(discount) || discount < 0) {
        this.discountError = this.$t('common.invalid-discount')
        return
      }

      if (discount > amount) {
        this.discountError = this.$t('common.discount-exceeds-amount')
        return
      }

      try {
        this.loading = true

        const payload = {
          discount,
        }

        await this.$http.put(`/invoices/${this.discountForm.id}`, payload)

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('common.discount-applied-successfully'),
          },
        })

        this.$bvModal.hide('modal-apply-discount')

        // Refresh data
        this.$emit('onChangeStatus')
      } catch (error) {
        console.error('Error applying discount:', error)

        // Handle validation errors (400)
        if (error.response && error.response.status === 400) {
          const errors = error.response.data

          // Check if discount field has errors
          if (errors.discount && Array.isArray(errors.discount)) {
            this.discountError = errors.discount[0]
          } else {
            this.discountError = this.$t('common.discount-apply-error')
          }
        } else {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('common.error'),
              variant: 'danger',
              text: this.$t('common.discount-apply-error'),
            },
          })
        }
      } finally {
        this.loading = false
      }
    },

    showNICUInvoice(item) {
      console.log(item)
      this.invoice = item
      console.log('this.invoice', this.invoice)
      this.$bvModal.show(`modal-confirm-print-${item.id}`)
    },

    async printInvoice() {
      try {
        this.loading = true

        const url = `/invoices?filter[id]=${this.invoice.id}&include=patientCard,patientCardIncubator,patientCardIncubator.incubator`
        await this.getList(url)

        if (
          this.all_data
          && this.all_data.data
          && this.all_data.data.length > 0
        ) {
          this.invoice = this.all_data.data[0]

          await this.$nextTick()

          setTimeout(() => {
            this.generatePdf()
            this.changeStatusToPrintedInvoices()
          }, 100)
        }
      } catch (error) {
        console.error('Failed to print invoice:', error)
      } finally {
        this.loading = false
      }
    },

    generatePdf() {
      this.isGenerating = true
      const content = this.$refs.printContent
      if (!content) {
        this.isGenerating = false
        return
      }

      // Get the innerHTML of the NICUInvoice component
      const invoiceElement = content.querySelector('.nicu-invoice')
      const htmlContent = invoiceElement ? invoiceElement.innerHTML : content.innerHTML

      // Get all styles from the document
      const styles = Array.from(document.styleSheets)
        .map(styleSheet => {
          try {
            return Array.from(styleSheet.cssRules)
              .map(rule => rule.cssText)
              .join('\n')
          } catch (e) {
            return ''
          }
        })
        .join('\n')

      const fullHtmlContent = `
<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>NICU Invoice</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
      padding: 12px;
      color: #111827;
      line-height: 1.4;
      direction: rtl;
    }
    .nicu-invoice {
      display: block !important;
      visibility: visible !important;
      background: white;
    }
    .d-none {
      display: block !important;
    }
    .invoice-container {
      background: white;
      color: black;
      padding: 20px;
      font-family: Arial, sans-serif;
      width: 210mm;
      min-height: 297mm;
    }
    @media print {
      body {
        padding: 10mm;
      }
      @page {
        size: A4;
        margin: 15mm;
      }
      .nicu-invoice {
        display: block !important;
        visibility: visible !important;
      }
      .d-none {
        display: block !important;
      }
      .invoice-container {
        max-width: 100% !important;
        width: 100% !important;
        padding: 20px !important;
        page-break-after: auto;
        page-break-inside: avoid;
      }
      table {
        page-break-inside: avoid;
      }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
    }
    ${styles}
  </style>
</head>
<body>
  ${htmlContent}
  <script>
    window.onload = function() {
      window.print();
    };
  <\/script>
</body>
</html>
      `

      // Open in a new tab/window and write content
      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        // eslint-disable-next-line no-alert
        window.alert(
          this.$t('common.popup-blocked') || 'Please allow popups for this site',
        )
        this.isGenerating = false
        return
      }

      // Write content to the new window
      printWindow.document.open()
      printWindow.document.write(fullHtmlContent)
      printWindow.document.close()

      // Focus on the new window after a short delay to ensure it's loaded
      setTimeout(() => {
        printWindow.focus()
      }, 100)

      // Listen for print window close
      const checkClosed = setInterval(() => {
        if (printWindow.closed) {
          clearInterval(checkClosed)
          this.isGenerating = false
        }
      }, 500)

      // Also handle after print dialog closes
      printWindow.onbeforeunload = () => {
        clearInterval(checkClosed)
        this.isGenerating = false
      }

      // Reset generating state after a delay
      setTimeout(() => {
        this.isGenerating = false
      }, 1000)
    },

    changeStatusToPrintedInvoices() {
      console.log('changeStatusToPrintedInvoices')
      this.params.form = { status: 'printed' }
      this.params.path = `/invoices/${this.invoice.id}/changeStatus`
      this.$store
        .dispatch('put', this.params)
        .then(response => {
          this.$emit('onChangeStatus')
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: `${this.$t('common.success')}`,
              variant: 'success',
              text: `${response.data.message}`,
            },
          })
        })
        .catch(error => {
          if (error.response.status === 400) {
            for (const item in error.response.data) {
              if (error.response.data.hasOwnProperty(item)) {
                this.$toast({
                  component: ToastificationContent,
                  position: 'top-right',
                  props: {
                    title: 'validation error',
                    variant: 'danger',
                    text: error.response.data[item][0],
                  },
                })
              }
            }
          }
        })
    },

    // Format datetime for API (convert from "YYYY-MM-DDTHH:mm" to "YYYY-MM-DD HH:mm:ss")
    formatDateTimeForAPI(dateTime) {
      if (!dateTime) return null
      return `${dateTime.replace('T', ' ')}:00`
    },

    // Open create invoice modal
    openCreateInvoiceModal() {
      const invoicesData = getNestedProperty(this.invoices, 'data', [])
      if (invoicesData.length === 0) {
        this.$toast.error(this.$t('incubator.no-invoices-found'))
        return
      }

      const firstInvoice = invoicesData[0]

      // Reset form
      this.newInvoiceForm = {
        patient_card_id: getNestedProperty(firstInvoice, 'patient_card.id', ''),
        patient_card_incubator_id: getNestedProperty(firstInvoice, 'patient_card_incubator_id', ''),
        amount: '',
        paid_until: '',
        description: '',
        type: 'invoice_nicu',
      }
      this.newInvoiceError = { amount: '', paid_until: '' }

      // Set display names
      this.newInvoicePatientName = this.formatPatientName(firstInvoice)
      this.newInvoiceIncubatorName = getNestedProperty(
        firstInvoice,
        'patient_card_incubator.incubator.name',
        'N/A',
      )

      // Show modal
      this.$bvModal.show('modal-create-invoice')
    },

    // Create new invoice
    async createNewInvoice() {
      this.loadingInvoice = true
      this.newInvoiceError.amount = ''
      this.newInvoiceError.paid_until = ''

      // Validate amount
      if (!this.newInvoiceForm.amount || this.newInvoiceForm.amount <= 0) {
        this.newInvoiceError.amount = this.$t('validation.required')
        this.loadingInvoice = false
        return
      }

      // Validate paid_until
      if (!this.newInvoiceForm.paid_until) {
        this.newInvoiceError.paid_until = this.$t('validation.required')
        this.loadingInvoice = false
        return
      }

      try {
        // Prepare payload in the format API expects
        const payload = {
          invoices: [
            {
              patient_card_id: this.newInvoiceForm.patient_card_id,
              patient_card_incubator_id: this.newInvoiceForm.patient_card_incubator_id,
              amount: parseFloat(this.newInvoiceForm.amount),
              paid_until: this.formatDateTimeForAPI(this.newInvoiceForm.paid_until),
              type: this.newInvoiceForm.type,
              ...(this.newInvoiceForm.description && {
                description: this.newInvoiceForm.description,
              }),
            },
          ],
        }

        await this.$http.post('/invoices', payload)

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('incubator.invoice-created-successfully'),
          },
        })

        // Close modal and refresh data
        this.$bvModal.hide('modal-create-invoice')

        // Emit event to refresh parent data
        this.$emit('onChangeStatus')
      } catch (error) {
        console.error('Error creating invoice:', error)

        // Handle validation errors (400)
        if (error.response && error.response.status === 400) {
          const errors = error.response.data

          // Check for specific field errors (including nested errors like invoices.0.field)
          if (errors.amount && Array.isArray(errors.amount)) {
            this.newInvoiceError.amount = errors.amount[0]
          }
          if (errors['invoices.0.amount'] && Array.isArray(errors['invoices.0.amount'])) {
            this.newInvoiceError.amount = errors['invoices.0.amount'][0]
          }

          if (errors.paid_until && Array.isArray(errors.paid_until)) {
            this.newInvoiceError.paid_until = errors.paid_until[0]
          }
          if (errors['invoices.0.paid_until'] && Array.isArray(errors['invoices.0.paid_until'])) {
            this.newInvoiceError.paid_until = errors['invoices.0.paid_until'][0]
          }

          // Show generic error if no specific field errors were set
          if (!this.newInvoiceError.amount && !this.newInvoiceError.paid_until) {
            // Try to show any other error message from the response
            const firstErrorKey = Object.keys(errors)[0]
            const firstErrorMessage = Array.isArray(errors[firstErrorKey])
              ? errors[firstErrorKey][0]
              : errors[firstErrorKey]

            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: this.$t('common.error'),
                variant: 'danger',
                text: firstErrorMessage || this.$t('common.error-occurred'),
              },
            })
          }
        } else {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('common.error'),
              variant: 'danger',
              text: this.$t('common.error-occurred'),
            },
          })
        }
      } finally {
        this.loadingInvoice = false
      }
    },
    async bulkPrintSummary() {
      if (this.selectedInvoices.length === 0 || !this.canBulkPrint) return

      const invoicesToPrint = this.selectedInvoices
      if (invoicesToPrint.length === 0) return

      this.bulkPrintInvoicesData = []
      this.isGeneratingPdf = true

      try {
        const invoiceIds = invoicesToPrint.map(inv => inv.id).join(',')
        const includeString = 'service,surgery,patientCard,doctor,invoiceItems,patientCardIncubator,patientCardIncubator.incubator'

        const url = `/invoices?filter[id]=${invoiceIds}&include=${includeString}&take=1000`
        const response = await this.$http.get(url)

        if (response.data?.data && response.data.data.length > 0) {
          this.bulkPrintInvoicesData = response.data.data

          await this.$nextTick()
          await this.$nextTick()

          // Use the new shared PDF component
          setTimeout(() => {
            if (this.$refs.nicuInvoicesListPdf) {
              this.$refs.nicuInvoicesListPdf.generatePdf()
            }
          }, 100)

          // Safety timeout: ensure loading state clears even if preview is closed manually
          setTimeout(() => {
            if (this.isGeneratingPdf) {
              this.isGeneratingPdf = false
            }
          }, 15000)
        }
      } catch (error) {
        console.error('Error fetching invoices for bulk print:', error)
        this.isGeneratingPdf = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-occurred'),
          },
        })
      }
    },
    async bulkPrintDetailed() {
      if (this.selectedInvoices.length === 0 || !this.canBulkPrint) return

      const invoicesToPrint = this.selectedInvoices
      if (invoicesToPrint.length === 0) return

      this.bulkPrintInvoicesData = []
      this.isGenerating = true

      try {
        const invoiceIds = invoicesToPrint.map(inv => inv.id).join(',')
        const includeString = 'service,surgery,patientCard,doctor,invoiceItems,patientCardIncubator,patientCardIncubator.incubator'

        const url = `/invoices?filter[id]=${invoiceIds}&include=${includeString}&take=1000`
        const response = await this.$http.get(url)

        if (response.data?.data && response.data.data.length > 0) {
          this.bulkPrintInvoicesData = response.data.data

          // Wait for DOM to render all invoice components
          await this.$nextTick()
          await this.$nextTick()

          // Additional delay to ensure all styles are loaded
          setTimeout(() => {
            this.generateBulkPdf()
          }, 500)
        }
      } catch (error) {
        console.error('Error fetching invoices for bulk print:', error)
        this.isGenerating = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-occurred'),
          },
        })
      }
    },

    generateBulkPdf() {
      const bulkContainer = document.getElementById('BulkPrintNICUInvoices')
      if (!bulkContainer) {
        this.isGenerating = false
        return
      }

      // Get all invoice elements
      const invoiceElements = bulkContainer.querySelectorAll('.nicu-invoice')
      if (invoiceElements.length === 0) {
        this.isGenerating = false
        return
      }

      // Collect HTML content from all invoices with proper page breaks
      const invoicesArray = Array.from(invoiceElements)
      const invoicesHtml = invoicesArray
        .map((el, index) => {
          const html = el.innerHTML
          // Add page break only between invoices, not after the last one
          if (index < invoicesArray.length - 1) {
            return `<div class="invoice-wrapper" style="page-break-after: always;">${html}</div>`
          }
          return `<div class="invoice-wrapper">${html}</div>`
        })
        .join('')

      // Get all styles from the document
      const styles = Array.from(document.styleSheets)
        .map(styleSheet => {
          try {
            return Array.from(styleSheet.cssRules)
              .map(rule => rule.cssText)
              .join('\n')
          } catch (e) {
            return ''
          }
        })
        .join('\n')

      const fullHtmlContent = `
<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>NICU Invoices - Bulk Print</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
      padding: 12px;
      color: #111827;
      line-height: 1.4;
      direction: rtl;
    }
    .nicu-invoice {
      display: block !important;
      visibility: visible !important;
      background: white;
    }
    .d-none {
      display: block !important;
    }
    .invoice-wrapper {
      page-break-inside: avoid;
    }
    .invoice-container {
      background: white;
      color: black;
      padding: 20px;
      font-family: Arial, sans-serif;
      width: 100%;
      page-break-inside: avoid;
    }
    @media print {
      body {
        padding: 10mm;
      }
      @page {
        size: A4;
        margin: 15mm;
      }
      .nicu-invoice {
        display: block !important;
        visibility: visible !important;
      }
      .d-none {
        display: block !important;
      }
      .invoice-wrapper {
        page-break-inside: avoid;
      }
      .invoice-wrapper:not(:last-child) {
        page-break-after: always;
      }
      .invoice-container {
        max-width: 100% !important;
        width: 100% !important;
        padding: 20px !important;
        page-break-inside: avoid;
      }
      table {
        page-break-inside: avoid;
      }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
    }
    ${styles}
  </style>
</head>
<body>
  ${invoicesHtml}
  <script>
    window.onload = function() {
      window.print();
    };
  <\/script>
</body>
</html>
      `

      // Open in a new tab/window and write content
      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        // eslint-disable-next-line no-alert
        window.alert(
          this.$t('common.popup-blocked') || 'Please allow popups for this site',
        )
        this.isGenerating = false
        return
      }

      // Write content to the new window
      printWindow.document.open()
      printWindow.document.write(fullHtmlContent)
      printWindow.document.close()

      // Update status after print
      setTimeout(() => {
        this.updateBulkPrintStatus()
      }, 1000)

      // Focus on the new window after a short delay to ensure it's loaded
      setTimeout(() => {
        printWindow.focus()
      }, 100)

      // Listen for print window close
      const checkClosed = setInterval(() => {
        if (printWindow.closed) {
          clearInterval(checkClosed)
          this.isGenerating = false
        }
      }, 500)

      // Also handle after print dialog closes
      printWindow.onbeforeunload = () => {
        clearInterval(checkClosed)
        this.isGenerating = false
      }

      // Reset generating state after a delay
      setTimeout(() => {
        this.isGenerating = false
      }, 1000)
    },
    async updateBulkPrintStatus() {
      if (this.selectedInvoices.length === 0) return

      try {
        const invoicesPayload = this.selectedInvoices.map(invoice => ({
          id: invoice.id,
          status: 'printed',
        }))

        await this.$http.post('/invoices/bulkUpdateStatus', {
          invoices: invoicesPayload,
        })

        this.clearSelection()
        this.bulkPrintInvoicesData = []
        this.$emit('onChangeStatus')
      } catch (error) {
        console.error('Error updating bulk print status:', error)
      }
    },
    getBulkPatientData(invoice) {
      if (invoice && invoice.patient_card) {
        return {
          ...invoice.patient_card,
          patient: invoice.patient_card.patient
            ? { ...invoice.patient_card.patient }
            : null,
        }
      }
      return this.patientData || {}
    },
    getBulkIncubatorName(invoice) {
      return getNestedProperty(invoice, 'patient_card_incubator.incubator.name', 'N/A')
    },
  },
}
</script>

<style>
.hidden {
  display: none;
}

.print-page-break {
  page-break-after: always;
  display: block;
}
</style>

<style scoped>
/* Table horizontal scroll - always visible */
.table-responsive,
.b-table-responsive,
.table > .table-responsive {
  overflow-x: scroll !important;
  overflow-y: visible !important;
  -webkit-overflow-scrolling: touch;
}

/* Make scrollbar always visible and styled - Webkit browsers */
.table-responsive::-webkit-scrollbar,
.b-table-responsive::-webkit-scrollbar {
  height: 14px !important;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.table-responsive::-webkit-scrollbar-track,
.b-table-responsive::-webkit-scrollbar-track {
  background-color: #f5f5f5;
  border-radius: 8px;
  margin: 0 10px;
}

.table-responsive::-webkit-scrollbar-thumb,
.b-table-responsive::-webkit-scrollbar-thumb {
  background-color: #888;
  border-radius: 8px;
  border: 2px solid #f5f5f5;
}

.table-responsive::-webkit-scrollbar-thumb:hover,
.b-table-responsive::-webkit-scrollbar-thumb:hover {
  background-color: #555;
}

.pdf-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.pdf-loading-content {
  background: white;
  padding: 30px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* For Firefox */
.table-responsive,
.b-table-responsive {
  scrollbar-width: auto !important;
  scrollbar-color: #888 #f5f5f5;
}

.hidden {
  display: none;
}
</style>
